"use client"

import { useCallback } from 'react'

import { useWorkflow } from '@/context/workflow-context-simple'
import { buildFilterForField } from '@/utils/column-type-helpers'

// item[Column] ?? item[column] — the search/grid rows carry PascalCase index fields, everything else
// camelCase, so both are tried.
const getCellValue = (item: Record<string, any>, columnCode: string): any => {
  const camelCaseKey = columnCode.charAt(0).toLowerCase() + columnCode.slice(1)
  return item[columnCode] ?? item[camelCaseKey] ?? undefined
}

export interface RowNavigationOptions {
  /** Called after the in-grid jump is set up (the worker search closes the modal + clears the tab here). */
  onNavigate?: () => void
  /** Fallback when there is no onNavigate — just close. */
  onClose?: () => void
  /** Pre-onboarding / request rows can't be reached by the grid setters; the caller switches tabs. */
  onNavigateToRequestTab?: (documentType: string, item: Record<string, any>) => void
}

/**
 * The grid's row → entity navigation, extracted from the worker global search so any surface (e.g. the
 * messages inbox) lands on a record EXACTLY the same way — one source of truth, no drift.
 *
 * WorkOrder/Assignment rows jump in-grid: resolve the row's Stage to a stage, build the same
 * worker/client/WO# stage filters the search uses, and set stage + filters over `useWorkflow`. Request
 * rows (`isRequestRow`) run isolated providers these setters can't reach, so they hand off to
 * `onNavigateToRequestTab`.
 */
export function useWorkflowRowNavigation() {
  const {
    configuration,
    stages,
    setCurrentStage,
    setStageFilters,
    stageFilters,
    setCurrentPage,
    refreshData,
    temporaryQueryFilter,
    clearTemporaryFilter,
  } = useWorkflow()

  const navigateToRow = useCallback(
    (item: Record<string, any>, opts: RowNavigationOptions = {}) => {
      const { onNavigate, onClose, onNavigateToRequestTab } = opts

      // Pre-onboarding rows route through the caller — their tabs run isolated providers this context's
      // stage/filter setters can't reach.
      if (item.isRequestRow) {
        const docType = item.documentType
        if (docType && onNavigateToRequestTab) onNavigateToRequestTab(docType, item)
        return
      }

      const itemStage = getCellValue(item, 'Stage')
      if (!itemStage) return

      const targetStage = stages.find(
        (s: any) => s.stageCode.toLowerCase() === String(itemStage).toLowerCase()
      )
      if (!targetStage || !configuration?.fieldsInUse) return

      const stageId = targetStage.stageId

      // Same fields, same priority, same shared buildFilterForField as the worker search, so the
      // cross-tab handoff produces identical filters.
      const newFilters = [
        { columnCode: 'WorkerFullName', value: getCellValue(item, 'WorkerFullName') },
        { columnCode: 'ClientName', value: getCellValue(item, 'ClientName') },
        { columnCode: 'FboWorkOrderNumber', value: getCellValue(item, 'FboWorkOrderNumber') },
      ]
        .map(({ columnCode, value }) => {
          const fieldConfig = configuration.fieldsInUse.find((f: any) => f.columnCode === columnCode)
          if (!fieldConfig) return null
          const filterResult = buildFilterForField(fieldConfig, value)
          if (!filterResult) return null
          return { stageId, fieldConfigurationId: fieldConfig.fieldConfigurationId, ...filterResult }
        })
        .filter(Boolean) as Array<{ stageId: string; fieldConfigurationId: string; filter: string; values: any }>

      const existingFilters = stageFilters.filter((f: any) => f.stageId !== stageId)
      const updatedFilters = [...existingFilters, ...newFilters]

      setCurrentPage(1)
      setCurrentStage(targetStage.stageCode as any)
      setStageFilters(updatedFilters)

      // Same-stage jumps don't clear a surviving `?worker=` scope, which would AND with the row filters
      // and empty the grid — so drop any temporary scope, exactly as the search does.
      const hadTemporaryFilter = !!temporaryQueryFilter
      if (hadTemporaryFilter) clearTemporaryFilter()

      if (onNavigate) onNavigate()
      else if (onClose) onClose()

      // Skipped when we just cleared a scope: the captured refreshData closure still holds it and would
      // re-append it. The grid's own fetch effect handles that case with fresh state.
      if (!hadTemporaryFilter) {
        setTimeout(() => {
          const stageFilterStrings = newFilters.map((f) => f.filter).filter(Boolean)
          const baseFilter = `Stage eq '${targetStage.stageCode}'`
          const filterArray =
            stageFilterStrings.length > 0
              ? [`${baseFilter} and ${stageFilterStrings.join(' and ')}`]
              : [baseFilter]
          refreshData(filterArray)
        }, 100)
      }
    },
    [
      stages,
      configuration,
      stageFilters,
      setCurrentStage,
      setStageFilters,
      setCurrentPage,
      refreshData,
      temporaryQueryFilter,
      clearTemporaryFilter,
    ]
  )

  return { navigateToRow }
}
